/** Pure derivations behind the To-pull lane: which scope members are planned
 *  vs undecided / left out / other-section, where their ghosts sit on the day
 *  axis, the "Pull all remaining" placement batch, and the delete-guard's
 *  left-behind orphan ties. No React, no IO.
 */
import type {
  DepGraph,
  DraftPayload,
  InScopeOrphan,
  PendingWorkCodeAdd,
  ScopeMember,
} from "../../../models";
import { ActivityStatus, formatLagSuffix } from "../../../utils/p6";
import { activityLabel } from "../depCore/graphRows";
import type { ZoneRow } from "../draft/types";
import { zoneKey } from "../draft/zones";
import { parseMemberName } from "./memberParsing";
import type { LaneMember, LaneMemberState } from "./ScopeLedger";

export type MemberState = LaneMemberState | "planned";

const eqi = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/** Decide every scope member's state against the section + the draft. Members
 *  staged for delete are EXCLUDED (no entry). PLANNED wins over everything
 *  (a completed member tagged into this section is board work, not lane
 *  work); then completed → foreign → kept → unresolved. */
export function deriveMemberStates(
  members: ScopeMember[],
  sectionName: string,
  placedBarIds: Set<number>,
  draft: DraftPayload,
): Map<number, MemberState> {
  const takted = new Set(draft.pending_taktifications.map((t) => t.activity_id));
  const removed = new Set(
    draft.pending_work_code_removals.map((r) => r.activity_id),
  );
  const untaktZones = new Set(
    draft.pending_untaktifications.map((z) => zoneKey(z.takt_a, z.takt_b, z.takt_c)),
  );
  const keeps = new Set(draft.member_keeps);
  const deletes = new Set(draft.pending_activity_deletes.map((d) => d.activity_id));

  const out = new Map<number, MemberState>();
  for (const m of members) {
    if (deletes.has(m.activity_id)) continue;
    const stagedIn = takted.has(m.activity_id) && !removed.has(m.activity_id);
    const committedTagged =
      m.takt_section_name === sectionName &&
      Boolean(m.takt_work) &&
      Boolean(m.takt_b) &&
      Boolean(m.takt_c) &&
      !removed.has(m.activity_id) &&
      !untaktZones.has(zoneKey(m.takt_a, m.takt_b, m.takt_c));
    if (placedBarIds.has(m.activity_id) || stagedIn || committedTagged) {
      out.set(m.activity_id, "planned");
      continue;
    }
    if ((m.status ?? "").toUpperCase() === ActivityStatus.Complete.toUpperCase()) {
      out.set(m.activity_id, "completed");
      continue;
    }
    if (m.takt_section_name && m.takt_section_name !== sectionName) {
      out.set(m.activity_id, "foreign");
      continue;
    }
    out.set(m.activity_id, keeps.has(m.activity_id) ? "kept" : "unresolved");
  }
  return out;
}

/** The member's board span at its real dates: start column from restart_date,
 *  duration from restart→reend (falling back to original_duration). */
export function memberSpan(
  m: ScopeMember,
  calendarIndexFor: (iso: string | null) => number,
): { startIndex: number; durationDays: number } {
  const startIndex = calendarIndexFor(m.restart_date);
  let durationDays = 1;
  if (m.restart_date && m.reend_date) {
    const endIdx = calendarIndexFor(m.reend_date);
    if (endIdx >= startIndex) durationDays = endIdx - startIndex + 1;
  } else if (m.original_duration != null) {
    durationDays = Math.max(1, Math.round(m.original_duration));
  }
  return { startIndex, durationDays };
}

export interface AutoPlacement {
  member: ScopeMember;
  code: string;
  zone: { takt_a: string; takt_b: string; takt_c: string };
  startIndex: number;
  durationDays: number;
}

/** "Pull all remaining": resolve each undecided member's code (its committed
 *  Takt_Work, else the « CODE » suffix) and zone/level (the name's
 *  "| ZONE - LEVEL" segment), matched case-insensitively against existing
 *  board rows (unmatched zone-levels get a fresh zone under `defaultTaktA`).
 *  Unparseable members are skipped — they stay undecided. */
export function buildAutoPlacements(args: {
  candidates: LaneMember[];
  rows: ZoneRow[];
  defaultTaktA: string;
  /** Match a raw code against the section palette (case-insensitive); the
   *  returned activityKey is the canonical code to stage. */
  matchCode: (raw: string) => { activityKey: string } | undefined;
  colorFor: (code: string) => string;
}): { placements: AutoPlacement[]; codeAdds: PendingWorkCodeAdd[] } {
  const placements: AutoPlacement[] = [];
  const codeAdds = new Map<string, PendingWorkCodeAdd>();
  for (const lm of args.candidates) {
    const parsed = parseMemberName(lm.member.task_name);
    const rawCode = lm.member.takt_work || parsed.code;
    if (!rawCode || !parsed.zone || !parsed.level) continue;
    const { zone: parsedZone, level: parsedLevel } = parsed;
    const row = args.rows.find(
      (r) => eqi(r.takt_b, parsedZone) && eqi(r.takt_c, parsedLevel),
    );
    const zone = row
      ? { takt_a: row.takt_a, takt_b: row.takt_b, takt_c: row.takt_c }
      : { takt_a: args.defaultTaktA, takt_b: parsedZone, takt_c: parsedLevel };
    const chip = args.matchCode(rawCode);
    const code = chip?.activityKey ?? rawCode;
    if (!chip && !codeAdds.has(code)) {
      codeAdds.set(code, {
        code,
        prefix: parsed.workName || code,
        label: parsed.workName || code,
        color: args.colorFor(code),
        default_duration_days: lm.durationDays,
      });
    }
    placements.push({
      member: lm.member,
      code,
      zone,
      startIndex: lm.startIndex,
      durationDays: lm.durationDays,
    });
  }
  return { placements, codeAdds: Array.from(codeAdds.values()) };
}

/** Ties between activities staged for delete and KEPT / UNRESOLVED scope
 *  members — work left behind in the pulled nodes that would lose its logic. */
export function buildInScopeOrphans(
  g: DepGraph,
  deletedIds: Set<string>,
  stateById: Map<number, MemberState>,
  memberNameById: Map<number, string>,
): InScopeOrphan[] {
  const orphanState = (gid: string): "kept" | "unresolved" | null => {
    const n = Number(gid);
    if (!Number.isFinite(n)) return null;
    const st = stateById.get(n);
    return st === "kept" || st === "unresolved" ? st : null;
  };
  const out: InScopeOrphan[] = [];
  for (const [succId, edges] of Object.entries(g.edges)) {
    for (const e of edges) {
      const succDeleted = deletedIds.has(succId);
      const predDeleted = deletedIds.has(e.pred_id);
      if (succDeleted === predDeleted) continue;
      const survivorId = succDeleted ? e.pred_id : succId;
      const state = orphanState(survivorId);
      if (!state) continue;
      const deletedId = succDeleted ? succId : e.pred_id;
      const survivorNode = g.activities[survivorId];
      const deletedNode = g.activities[deletedId];
      out.push({
        memberId: survivorId,
        memberName: survivorNode
          ? activityLabel(survivorNode)
          : memberNameById.get(Number(survivorId)) ?? `#${survivorId}`,
        deletedName: deletedNode ? activityLabel(deletedNode) : `#${deletedId}`,
        rel: `${e.type}${formatLagSuffix(e.lag_hours)}`,
        state,
      });
    }
  }
  return out;
}

/** Deleting an activity also retires its lane decisions: a keep or claim of a
 *  deleted member is stale state (publish skips it anyway). */
export function pruneScopeDecisions(
  draft: DraftPayload,
  deletedIds: number[],
): DraftPayload {
  if (deletedIds.length === 0) return draft;
  const ids = new Set(deletedIds);
  const keeps = draft.member_keeps.filter((id) => !ids.has(id));
  const claims = draft.pending_section_claims.filter(
    (c) => !ids.has(c.activity_id),
  );
  if (
    keeps.length === draft.member_keeps.length &&
    claims.length === draft.pending_section_claims.length
  ) {
    return draft;
  }
  return { ...draft, member_keeps: keeps, pending_section_claims: claims };
}
